import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import {
  Button, DialogActions, DialogContent, Typography, TextField, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { translate } from 'utils';
import { FormElement, RequiredSelectHack } from 'components';
import { useFetch } from 'hooks';
import { DataTecneaService } from 'services';
import { faDesktopAlt } from '@fortawesome/pro-regular-svg-icons';
import { ACCESS_APPS } from 'utils/constants';
import shortid from 'shortid';
import { useForm, Controller } from 'react-hook-form';
import { HeaderModal } from './_HeaderModal';

export const AddSubscriptionModal = ({
                                       onConfirm, onClose, currentSub
                                     }) => {
  const {
 control, handleSubmit, watch, formState: { errors }
} = useForm({
    defaultValues: {
      accountsNeeded: 1,
      appSelected: null,
      attestationNumber: ''
    }
  });

  const { response: allApplications, isLoading } = useFetch(() => DataTecneaService.getActiveApplications(), []);
  const allApps = allApplications.map(app => app.name);
  const availableApps = allApps.filter(app => !currentSub.find(current => current.application === app));
  const appAlreadyAvailable = allApps.length === 0
      ? ACCESS_APPS
      : ACCESS_APPS.filter(access => allApps.some(app => app === access));

  const appSelected = watch('appSelected');

  const onSubmit = data => {
    if (!data.appSelected) return null;

    onConfirm({
      application: data.appSelected.value,
      accountsNeeded: data.accountsNeeded,
      attestationNumber: data.attestationNumber || null
    });
    return onClose();
  };

  const transformToSelectItem = useCallback(apps => apps.map(app => ({
    value: app,
    label: app
  })), []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <HeaderModal onClose={onClose}>
        <FontAwesomeIcon icon={faDesktopAlt} />
        <Typography component="span">
          {translate('modalAddSubscription.title')}
        </Typography>
      </HeaderModal>

      <DialogContent style={{ paddingTop: '10px' }}>
        {availableApps.length > 0 ? (
          <>
            <FormElement>
              <Controller
                      control={control}
                      name="appSelected"
                      render={({ field }) => (
                        <Select
                              {...field}
                              isClearable
                              isLoading={isLoading}
                              isSearchable
                              menuPortalTarget={document.body}
                              options={transformToSelectItem(availableApps)}
                              placeholder={translate('modalAddSubscription.selectApp')}
                              required
                              styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                          />
                      )}
                      rules={{ required: true }}
                  />
              <RequiredSelectHack value={appSelected || ''} />
            </FormElement>
            <FormElement>
              <Controller
                      control={control}
                      name="accountsNeeded"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              inputProps={{ min: 1 }}
                              label={translate('common.accountsNeeded')}
                              type="number"
                          />
                      )}
                  />
              {appSelected && appSelected.value === 'DATABILAN' && (
              <Controller
                          control={control}
                          name="attestationNumber"
                          render={({ field }) => (
                            <TextField
                                  {...field}
                                  error={!!errors.attestationNumber}
                                  helperText={errors.attestationNumber && 'Ce champ est requis'}
                                  label={translate('common.attestationNumber')}
                                  required
                              />
                          )}
                          rules={{ required: true }}
                      />
                  )}
            </FormElement>
          </>
          ) : (
            <>
              <Typography component="h2">{translate('modalAddSubscription.noApp')}</Typography>
              {appAlreadyAvailable.length > 0 && (
                <>
                  <br />
                  <Typography component="h3">{translate('modalAddSubscription.alreadyAvailable')}</Typography>
                  <List dense>
                    {appAlreadyAvailable.map(app => (
                      <ListItem dense key={shortid.generate()}>
                        <ListItemIcon style={{ minWidth: '30px' }}><FontAwesomeIcon icon={faDesktopAlt} /></ListItemIcon>
                        <ListItemText>{app}</ListItemText>
                      </ListItem>
                        ))}
                  </List>
                </>
                )}
            </>
          )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{translate('button.cancel')}</Button>
        <Button
              color="primary"
              disabled={availableApps.length === 0}
              type="submit"
          >
          {translate('button.validate')}
        </Button>
      </DialogActions>
    </form>
  );
};

AddSubscriptionModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  currentSub: PropTypes.arrayOf(PropTypes.shape({}))
};

AddSubscriptionModal.defaultProps = {
  currentSub: []
};
